import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";

import ToolTip from "../../../components/tooltip/ToolTip";
import {
  COLUMN_LABEL_COLOR_RATIO,
  formatHours,
  formatShare,
  mixWithWhite,
} from "./activityChartStyle";

/**
 * アクティビティ集計の帯グラフ。プロジェクトごとに 3 段の帯（プロジェクト帯 /
 * 項目ごと / ユーザーごと）を 1 ブロックにまとめ、時間の多い順に縦へ積む。
 *
 * 幅は**最大プロジェクトを全幅の基準**にし、2 件目以降のブロックは時間比の幅に
 * 縮める（20h が全幅なら 6h は 6/20 の幅）。縦に並んだブロック同士でも帯の長さ
 * がそのまま時間の比較になる。全体に占める割合はラベルの % でも読める。
 * 2・3 段目はプロジェクト内の時間比でブロック幅を分け合う。
 *
 * 【幅の正確さ】区間ボックス自身には padding・border を一切持たせない。
 * flex の幅分配は「コンテナ幅から各区間の padding・border・gap を差し引いた
 * 残り」を flex-grow 比で分けるため、区間に余白や枠を持たせると区間数の違う
 * 段同士で境界がずれる（例: 項目 5 区間 × padding 16px と、ユーザー 2 区間 ×
 * (padding 16px + 枠 2px) では先取り幅が異なる）。余白はラベル側（SegmentLabel
 * の内側 padding）に、区切り線・チップの枠はレイアウトに影響しない box-shadow
 * で描くことで、どの段も「累計時間の等しい位置で境界が揃う」ことを保証する。
 *
 * 見た目はタスクボード（テーブル）をそのまま横帯に写像する:
 * - プロジェクト帯: テーブルのカードヘッダーと同じ（バナー背景 + h3.heading-13
 *   + 字間 2.5px + バナーに応じた白黒の文字色）
 * - 項目ごと: テーブルの列ラベル行と同じ（工程配色を淡くした帯 + 黒文字）
 * - ユーザーごと: 担当者フィルターのチップと同じ（白地 + 担当者色の枠と文字 +
 *   pill 型）
 *
 * 各区間のラベルは「名前 時間（割合）」を帯の中に直接描く。入り切らない区間は
 * 「名前だけ」に短縮し、それも入らなければ隠す。ツールチップは `truncatedOnly`
 * で、フル表記が読めない（短縮・非表示の）区間にだけホバーで出す。
 * 右端の時間列・凡例・行ラベル列は持たない。
 *
 * チャートライブラリは使わない。軸もスケール計算も要らない一方、ラベルは日本語の
 * 担当者名・工程名で長さが不定なため、折り返し・省略の制御は HTML/CSS のほうが
 * 素直に書けるという判断（SVG だとテキスト処理が足枷になる）。
 */

/** 区間 1 個（名前と時間） */
export interface ActivitySegment {
  key: string;
  label: string;
  hours: number;
}

/** プロジェクト 1 件ぶんの帯データ（プロジェクト帯 + 項目ごと + ユーザーごと） */
export interface ActivityProjectBand {
  key: string;
  label: string;
  hours: number;
  /** プロジェクトバナーの背景（単色 or グラデーション） */
  background: string;
  /** バナー背景の上に置く文字色（淡色バナーのみ黒） */
  textColor: string;
  /** 項目（工程）ごとの内訳。合計はプロジェクトの hours と一致する */
  columnSegments: ActivitySegment[];
  /** ユーザーごとの内訳。合計はプロジェクトの hours と一致する */
  staffSegments: ActivitySegment[];
}

/** プロジェクト帯の高さ（テーブルのカードヘッダー相当） */
const PROJECT_ROW_HEIGHT = 40;

/** 項目ごと帯の高さ（テーブルの列ラベル行相当） */
const COLUMN_ROW_HEIGHT = 30;

/** ユーザーごと帯の高さ（フィルターチップ相当） */
const STAFF_ROW_HEIGHT = 24;

/** 3 段の間と区間の隙間。小さくして 1 つの大きな帯として読めるようにする */
const BAND_GAP = 2;

/** プロジェクトブロック同士の隙間（ブロックの区切りが読める程度に空ける） */
const BLOCK_GAP = 8;

const Blocks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${BLOCK_GAP}px;
  width: 100%;
`;

/**
 * プロジェクト 1 件ぶん（3 段で 1 つの大きな帯に見せる）。
 * 幅 = 最大プロジェクトに対する時間比。
 */
const Block = styled.div<{ $ratio: number }>`
  display: flex;
  flex-direction: column;
  gap: ${BAND_GAP}px;
  width: ${({ $ratio }) => $ratio * 100}%;
`;

const BandRow = styled.div`
  display: flex;
  width: 100%;
`;

/** 項目ごとの段。両端だけ角丸にする */
const ColumnRow = styled(BandRow)`
  border-radius: 4px;
  overflow: hidden;
`;

/** ユーザーごとの段。pill が繋がって見えないよう区間の間に隙間を空ける */
const StaffRow = styled(BandRow)`
  gap: ${BAND_GAP}px;
`;

/**
 * プロジェクト帯（テーブルのカードヘッダーと同じ見た目・ブロック全幅）。
 * 字間はテーブルの見出し（グローバル h3 ルールの letter-spacing 2.5px）と
 * 同じ値をここで指定し、ラベル（h3）と計測用ゴースト（span）の両方に効かせる。
 */
const ProjectBanner = styled.div<{ $background: string }>`
  width: 100%;
  height: ${PROJECT_ROW_HEIGHT}px;
  border-radius: 4px;
  background: ${({ $background }) => $background};
  letter-spacing: 2.5px;
`;

/**
 * 時間に比例した幅の flex 区間。flex-basis 0 で全幅を flex-grow（= 時間）の
 * 比で分配する。幅の正確さのため padding・border は持たない（冒頭コメント参照）。
 */
const proportional = `
  flex-shrink: 1;
  flex-basis: 0%;
  min-width: 0;
`;

/** 項目ごとの区間（テーブルの列ラベル行と同じ見た目）。区切りは白線 */
const ColumnSegment = styled.div<{ $hours: number; $background: string }>`
  ${proportional}
  flex-grow: ${({ $hours }) => $hours};
  height: ${COLUMN_ROW_HEIGHT}px;
  background: ${({ $background }) => $background};

  /* 区切り線。border だと幅分配から 1px 先取りされるので box-shadow で描く */
  & + & {
    box-shadow: inset 1px 0 0 #fff;
  }
`;

/** ユーザーごとの区間（担当者フィルターのチップと同じ見た目）。枠は box-shadow */
const StaffChip = styled.div<{ $hours: number; $color: string }>`
  ${proportional}
  flex-grow: ${({ $hours }) => $hours};
  height: ${STAFF_ROW_HEIGHT}px;
  border-radius: 999px;
  background: #fff;
  box-shadow: inset 0 0 0 1px ${({ $color }) => $color};
  color: ${({ $color }) => $color};
`;

/** ラベルの入れ物。区間いっぱいに広がり、余白はこの内側で取る */
const LabelBox = styled.div<{ $center?: boolean; $padding: number }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ $center }) => ($center ? "center" : "flex-start")};
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: 0 ${({ $padding }) => $padding}px;
  overflow: hidden;
`;

/**
 * 幅計測用の不可視ゴースト。left/right を LabelBox の padding に合わせ、
 * clientWidth = ラベルの表示可能幅、scrollWidth = 文言の必要幅になる。
 * フル表記のゴーストが見切れている区間は ToolTip の truncatedOnly 判定にも
 * 掛かる（= フルで読めない区間にだけツールチップが出る）。
 */
const Ghost = styled.span<{ $padding: number }>`
  position: absolute;
  left: ${({ $padding }) => $padding}px;
  right: ${({ $padding }) => $padding}px;
  visibility: hidden;
  overflow: hidden;
  white-space: nowrap;
  letter-spacing: inherit;
`;

/** 実際に見えるラベル。h3 で使うときに備え見出しの既定マージンを打ち消す */
const LabelText = styled.span`
  margin: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
`;

interface SegmentLabelProps {
  /** フル表記（名前 時間（割合）） */
  full: string;
  /** 短縮表記（名前のみ）。フルが入らない幅ではこちらを試す */
  short: string;
  /** ラベル左右の余白（px）。区間側は padding を持たないためここで取る */
  padding: number;
  className?: string;
  color?: string;
  center?: boolean;
  /** プロジェクト帯はテーブルの見出しと同じ h3 にする（スタイルの継承を揃える） */
  as?: "h3";
}

/**
 * 帯内ラベル。フル表記 → 名前のみ → 非表示 の順に、入る最長の表記を選ぶ。
 * 入るかどうかは不可視ゴースト 2 つ（フル / 名前のみ）の実測で判定し、
 * 幅の変化（期間切り替え・画面リサイズ）には ResizeObserver で追従する。
 */
const SegmentLabel: React.FC<SegmentLabelProps> = ({
  full,
  short,
  padding,
  className,
  color,
  center,
  as,
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const fullRef = useRef<HTMLSpanElement>(null);
  const shortRef = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState<"none" | "full" | "short">("none");

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    // +1 はサブピクセルレンダリングの誤差対策（ToolTip の truncatedOnly と同じ）
    const fits = (el: HTMLSpanElement | null) =>
      !!el && el.scrollWidth <= el.clientWidth + 1;
    const check = () =>
      setShown(
        fits(fullRef.current)
          ? "full"
          : fits(shortRef.current)
            ? "short"
            : "none",
      );
    check();
    const observer = new ResizeObserver(check);
    observer.observe(box);
    return () => observer.disconnect();
  }, [full, short]);

  return (
    <LabelBox ref={boxRef} $center={center} $padding={padding}>
      <Ghost ref={fullRef} aria-hidden className={className} $padding={padding}>
        {full}
      </Ghost>
      <Ghost
        ref={shortRef}
        aria-hidden
        className={className}
        $padding={padding}
      >
        {short}
      </Ghost>
      <LabelText
        {...(as ? { as } : {})}
        className={className}
        style={{ color, visibility: shown === "none" ? "hidden" : "visible" }}
      >
        {shown === "short" ? short : full}
      </LabelText>
    </LabelBox>
  );
};

/** 帯内ラベル・ツールチップ共通の「名前 時間（割合）」 */
const segmentText = (label: string, hours: number, base: number): string =>
  `${label} ${formatHours(hours)}（${formatShare(hours, base)}）`;

const segmentTooltip = (label: string, hours: number, base: number): string =>
  `${label}\n${formatHours(hours)}（${formatShare(hours, base)}）`;

export interface ActivityBarChartProps {
  projects: ActivityProjectBand[];
  /** 工程名 → 色。淡色化（列ラベル行と同じ混色）はこちらで行う */
  // eslint-disable-next-line no-unused-vars
  columnColorOf: (key: string) => string;
  /** 担当者名 → 色（チップの枠と文字に使う） */
  // eslint-disable-next-line no-unused-vars
  staffColorOf: (key: string) => string;
}

const ActivityBarChart: React.FC<ActivityBarChartProps> = ({
  projects,
  columnColorOf,
  staffColorOf,
}) => {
  const total = projects.reduce((sum, p) => sum + p.hours, 0);
  const maxHours = projects.reduce((max, p) => Math.max(max, p.hours), 0);
  if (projects.length === 0 || total <= 0) return null;

  return (
    <Blocks>
      {projects.map((p) => (
        <Block key={p.key} $ratio={p.hours / maxHours}>
          {/* プロジェクト帯。% は全体に占める割合 */}
          <ToolTip
            description={segmentTooltip(p.label, p.hours, total)}
            truncatedOnly
          >
            <ProjectBanner $background={p.background}>
              <SegmentLabel
                as="h3"
                className="heading-13"
                color={p.textColor}
                padding={16}
                full={segmentText(p.label, p.hours, total)}
                short={p.label}
              />
            </ProjectBanner>
          </ToolTip>

          {/* 項目ごと。% はプロジェクト内の割合 */}
          <ColumnRow>
            {p.columnSegments.map((s) => (
              <ToolTip
                key={s.key}
                description={segmentTooltip(s.label, s.hours, p.hours)}
                truncatedOnly
              >
                <ColumnSegment
                  $hours={s.hours}
                  $background={mixWithWhite(
                    columnColorOf(s.key),
                    COLUMN_LABEL_COLOR_RATIO,
                  )}
                >
                  <SegmentLabel
                    className="bodytext-13"
                    padding={8}
                    full={segmentText(s.label, s.hours, p.hours)}
                    short={s.label}
                  />
                </ColumnSegment>
              </ToolTip>
            ))}
          </ColumnRow>

          {/* ユーザーごと。% はプロジェクト内の割合 */}
          <StaffRow>
            {p.staffSegments.map((s) => (
              <ToolTip
                key={s.key}
                description={segmentTooltip(s.label, s.hours, p.hours)}
                truncatedOnly
              >
                <StaffChip $hours={s.hours} $color={staffColorOf(s.key)}>
                  <SegmentLabel
                    center
                    className="bodytext-13"
                    padding={8}
                    full={segmentText(s.label, s.hours, p.hours)}
                    short={s.label}
                  />
                </StaffChip>
              </ToolTip>
            ))}
          </StaffRow>
        </Block>
      ))}
    </Blocks>
  );
};

export default ActivityBarChart;
